import {FormControl, FormField} from 'platform/components';
import {HStack, VStack} from 'platform/foundation';

import {HTMLAttributes} from 'react';
import {UseFormReturn, useWatch} from 'react-hook-form';

import {DraggableCard} from '@dms/features/dnd';
import i18n from '@dms/i18n';
import {testIds} from '@dms/routes';

import {DOT_CHARACTER} from 'shared/web';

import {ChecklistFormValue} from '../types/ChecklistFormValue';

interface TaskCardProps {
  formApi: UseFormReturn<ChecklistFormValue>;
  control: FormControl<ChecklistFormValue>;
  index: number;
  handleRemove: () => void;
  isAlone: boolean;
  handleProps?: HTMLAttributes<unknown>;
}

export function TaskCard(props: TaskCardProps) {
  const title = useWatch({
    control: props.formApi.control,
    name: `tasks.${props.index}.description`,
  });

  const isMandatory = useWatch({
    control: props.formApi.control,
    name: `tasks.${props.index}.isMandatory`,
  });

  const fileRequired = useWatch({
    control: props.formApi.control,
    name: `tasks.${props.index}.fileRequired`,
  });

  const mandatoryText = isMandatory
    ? i18n.t('entity.settings.labels.mandatory')
    : i18n.t('entity.settings.labels.optional');

  const fileRequiredText = fileRequired
    ? i18n.t('entity.settings.labels.documentRequired')
    : i18n.t('entity.settings.labels.noDocumentRequired');

  const subtitle = [mandatoryText, fileRequiredText].join(` ${DOT_CHARACTER} `);

  return (
    <DraggableCard
      title={title || i18n.t('entity.settings.labels.newTask')}
      subtitle={subtitle}
      handleProps={props.handleProps}
      defaultExpanded
      data-testid={testIds.settings.generalChecklistCreate(`taskCard-${props.index}`)}
      actions={[
        {
          type: 'iconButton',
          icon: 'action/delete',
          severity: 'danger',
          onClick: props.handleRemove,
          isDisabled: props.isAlone,
        },
      ]}
    >
      <VStack spacing={6}>
        <FormField
          type="text"
          control={props.control}
          name={`tasks.${props.index}.description`}
          label={i18n.t('general.labels.name')}
          data-testid={testIds.settings.generalChecklistCreate(`taskName`)}
          isRequired
          isCounterVisible
          maxLength={255}
        />
        <HStack spacing={4}>
          <FormField
            type="checkbox"
            control={props.control}
            name={`tasks.${props.index}.isMandatory`}
            label={i18n.t('entity.settings.labels.mandatory')}
            data-testid={testIds.settings.generalChecklistCreate(`taskMandatory`)}
            isRequired
          />
          <FormField
            type="checkbox"
            control={props.control}
            name={`tasks.${props.index}.fileRequired`}
            label={i18n.t('entity.settings.labels.documentRequired')}
            data-testid={testIds.settings.generalChecklistCreate(`taskRequired`)}
            isRequired
          />
        </HStack>
      </VStack>
    </DraggableCard>
  );
}
